import { Box, CardContent, Typography, useTheme } from '@mui/material'
import Card from 'components/@extended/Card'
import CardFooter from 'components/cards/CardFooter'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React from 'react'
import { RowType } from 'types/comp-allocations'
import type { ExpandedCompensationPlan } from 'types/comp-plan'
import type { Period } from 'types/periods'

dayjs.extend(utc)

interface ProgressOverviewProps {
  plan: ExpandedCompensationPlan
  selectedPeriods: string[]
  periodMap: Record<string, Period>
}

const ProgressOverview: React.FC<ProgressOverviewProps> = ({ plan, selectedPeriods, periodMap }) => {
  const theme = useTheme()

  // Helper function to calculate plan validity proration for a specific period
  const calculatePlanValidityProration = (periodId: string): number => {
    const period = periodMap[periodId]
    if (!period) return 1

    const periodStart = dayjs.utc(period.startDate)
    const periodEnd = dayjs.utc(period.endDate)
    const totalPeriodDays = periodEnd.diff(periodStart, 'day') + 1

    // If plan has no validity dates, assume it's valid for the entire period
    if (!plan.plan.effectiveDate && !plan.plan.endDate) {
      return 1
    }

    // For backward compatibility: use period dates if plan dates are missing
    const planStartDate = plan.plan.effectiveDate ? dayjs.utc(plan.plan.effectiveDate) : periodStart
    const planEndDate = plan.plan.endDate ? dayjs.utc(plan.plan.endDate) : periodEnd

    // Calculate the overlap between plan validity and this specific period
    const effectiveStart = planStartDate.isAfter(periodStart) ? planStartDate : periodStart
    const effectiveEnd = planEndDate.isBefore(periodEnd) ? planEndDate : periodEnd

    // If there's no overlap, return 0
    if (effectiveEnd.isBefore(effectiveStart)) {
      return 0
    }

    // Calculate how many days the plan was valid in this period
    const planValidDays = effectiveEnd.diff(effectiveStart, 'day') + 1

    // Return the validity proration for this period
    return planValidDays / totalPeriodDays
  }
  const baseSalary = plan.plan.baseSalary

  const approvedAccruals = plan.progress || []

  // Calculate total earned considering both ledger and accruals
  const totalEarned = plan.plan.variableAllocations
    .flatMap((table) => table.rows)
    .filter((row) => selectedPeriods.includes(row.periodId!))
    .reduce((acc, row) => {
      // Find matching ledger or progress entry
      const ledgerEntry = plan.ledger?.find((entry) => entry.allocationRowId === row.id)
      const progressEntry = approvedAccruals.find((accrual) => accrual.allocationRowId === row.id)
      const period = periodMap[row.periodId!]

      // Use ledger if period is closed and ledger exists, otherwise use progress
      const amount =
        period.isClosed && ledgerEntry != null ? ledgerEntry.balance || 0 : progressEntry?.proratedPayoutAmount || 0

      return acc + amount
    }, 0)

  // Calculate on-target total with proper proration per row
  const { onTargetTotal, proratedOnTargetTotal } = plan.plan.variableAllocations
    .flatMap((table) => table.rows) // Get all rows from all allocation tables
    .filter(
      (row) =>
        selectedPeriods.includes(row.periodId!) &&
        row.rowType === RowType.CALCULATION_ROW && // Only include calculation rows
        !row.disabled // Exclude disabled rows
    )
    .reduce(
      (acc, row) => {
        // Calculate base target for this row
        const rowTarget = baseSalary * plan.plan.variablePercent * row.allocationPercentage

        // Calculate combined proration for this specific row's period
        const validityProration = calculatePlanValidityProration(row.periodId!)
        const combinedProration = plan.plan.periodProration * validityProration

        return {
          onTargetTotal: acc.onTargetTotal + rowTarget,
          proratedOnTargetTotal: acc.proratedOnTargetTotal + rowTarget * combinedProration,
        }
      },
      { onTargetTotal: 0, proratedOnTargetTotal: 0 }
    )

  const formattedTotalEarned = totalEarned.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const formattedPeriodOnTargetTotal = proratedOnTargetTotal.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const formattedOnTargetTotal = onTargetTotal.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const earnedPercentage = proratedOnTargetTotal > 0 ? Math.round((totalEarned / proratedOnTargetTotal) * 100) : 0

  // Determine if we should show prorated vs annual based on whether any proration is applied
  const hasProration = proratedOnTargetTotal !== onTargetTotal

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ mb: 1, ml: { xs: 1, sm: 3 } }}>
          <Typography sx={{ fontWeight: '500', fontSize: { xs: 16, sm: 18 }, mt: 1 }}>
            Total Variable Compensation Progress
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: '700', fontSize: { xs: 24, sm: 30 }, mt: 1 }}>
              ${formattedTotalEarned}
            </Typography>
            <Typography sx={{ fontWeight: '200', fontSize: { xs: 24, sm: 30 } }}>
              ({earnedPercentage}
              %)
            </Typography>
          </Box>
        </Box>
      </CardContent>
      <CardFooter>
        <Box sx={{ ml: { xs: 2, sm: 5 } }}>
          {hasProration ? (
            <Typography sx={{ color: theme.palette.grey[400], fontWeight: 'bold', fontSize: { xs: 12, sm: 14 } }}>
              {`On-Target Plan Variable Compensation: $${formattedPeriodOnTargetTotal}`}
            </Typography>
          ) : (
            <Typography sx={{ color: theme.palette.grey[400], fontWeight: 'bold', fontSize: { xs: 12, sm: 14 } }}>
              {`On-Target Annual Variable Compensation: $${formattedOnTargetTotal}`}
            </Typography>
          )}
        </Box>
      </CardFooter>
    </Card>
  )
}

export default ProgressOverview
